import React, { useState } from 'react';
import { CheckCircle, Clock, Droplets, Zap, Wind, Phone, User, Car, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { toast } from 'sonner';

export const RainOffer = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    carModel: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.carModel) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);

    // WhatsApp message
    const message = `Hi Think Mechanic, I saw your 20% rain damage offer. I want to book. (Message From Website)\n\nName: ${formData.name}\nPhone: ${formData.phone}\nCar Model: ${formData.carModel}`;
    const whatsappNumber = '+971566671468';
    const whatsappUrl = `https://wa.me/${whatsappNumber.replace(/\s/g, '')}?text=${encodeURIComponent(message)}`;

    // Open WhatsApp
    window.open(whatsappUrl, '_blank');
    
    toast.success('Redirecting to WhatsApp...');
    
    setTimeout(() => {
      setIsSubmitting(false);
      setFormData({ name: '', phone: '', carModel: '' });
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Header with Logo */}
      <header className="bg-black/95 backdrop-blur-md border-b border-zinc-800 py-4">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <img 
              src="https://customer-assets.emergentagent.com/job_think-mechanic/artifacts/glpenkiu_logo.png" 
              alt="Think Mechanic Logo" 
              className="h-12 w-auto object-contain"
            />
            <a 
              href="tel:+971566671468"
              className="flex items-center space-x-2 text-white hover:text-red-600 transition-colors"
            >
              <Phone className="w-5 h-5" />
              <span className="font-semibold">056 667 1468</span>
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, #ff0000 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        {/* Animated Rain Drops */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <Droplets className="absolute top-10 left-[10%] w-8 h-8 text-red-600/20 animate-pulse" />
          <Droplets className="absolute top-20 right-[15%] w-6 h-6 text-red-600/20 animate-pulse" style={{ animationDelay: '0.5s' }} />
          <Droplets className="absolute bottom-20 left-[20%] w-10 h-10 text-red-600/10 animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Urgency Badge */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center space-x-2 bg-red-600 px-6 py-3 rounded-full animate-pulse">
              <Clock className="w-5 h-5 text-white" />
              <span className="text-white font-bold text-lg">⏳ LIMITED TIME OFFER</span>
            </div>
          </div>

          {/* Main Headline */}
          <div className="text-center mb-12">
            <div className="inline-block mb-4">
              <div className="bg-gradient-to-r from-red-600 to-red-700 text-white text-6xl sm:text-7xl lg:text-8xl font-black px-8 py-4 rounded-2xl transform -rotate-2 shadow-2xl">
                20% OFF
              </div>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
              Rain Damage Repair
              <br />
              <span className="text-red-600">Labor Discount</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Don't let rain damage affect your German car's performance. Get expert repair at a special price!
            </p>
          </div>

          {/* Services Included */}
          <div className="grid md:grid-cols-3 gap-6 mb-12 max-w-4xl mx-auto">
            <Card className="bg-zinc-900 border-zinc-800 p-6 hover:border-red-600 transition-all duration-300">
              <div className="flex items-start space-x-4">
                <div className="bg-red-600/20 p-3 rounded-lg">
                  <Zap className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-2">Engine Check</h3>
                  <p className="text-gray-400 text-sm">Complete engine diagnostics and water damage assessment</p>
                </div>
              </div>
            </Card>

            <Card className="bg-zinc-900 border-zinc-800 p-6 hover:border-red-600 transition-all duration-300">
              <div className="flex items-start space-x-4">
                <div className="bg-red-600/20 p-3 rounded-lg">
                  <Zap className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-2">Electrical Repair</h3>
                  <p className="text-gray-400 text-sm">Fix water-damaged electrical systems and sensors</p>
                </div>
              </div>
            </Card>

            <Card className="bg-zinc-900 border-zinc-800 p-6 hover:border-red-600 transition-all duration-300">
              <div className="flex items-start space-x-4">
                <div className="bg-red-600/20 p-3 rounded-lg">
                  <Wind className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-2">AC Inspection</h3>
                  <p className="text-gray-400 text-sm">Air conditioning system check and repair</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Lead Form */}
          <div className="max-w-2xl mx-auto">
            <Card className="bg-gradient-to-br from-zinc-900 to-black border-2 border-red-600 p-8 shadow-2xl shadow-red-600/20">
              <div className="text-center mb-6">
                <h2 className="text-3xl font-bold text-white mb-2">Claim Your 20% Discount</h2>
                <p className="text-gray-400">Fill in your details and we'll contact you immediately</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="name" className="text-gray-300 mb-2 flex items-center">
                    <User className="w-4 h-4 mr-2 text-red-600" />
                    Your Name *
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter your name"
                    className="bg-black border-zinc-700 text-white placeholder:text-gray-500 focus:border-red-600 h-12 text-lg"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-gray-300 mb-2 flex items-center">
                    <Phone className="w-4 h-4 mr-2 text-red-600" />
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="+971 XX XXX XXXX"
                    className="bg-black border-zinc-700 text-white placeholder:text-gray-500 focus:border-red-600 h-12 text-lg"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="carModel" className="text-gray-300 mb-2 flex items-center">
                    <Car className="w-4 h-4 mr-2 text-red-600" />
                    Car Model *
                  </Label>
                  <Input
                    id="carModel"
                    type="text"
                    value={formData.carModel}
                    onChange={(e) => handleInputChange('carModel', e.target.value)}
                    placeholder="e.g., BMW X5, Mercedes C-Class"
                    className="bg-black border-zinc-700 text-white placeholder:text-gray-500 focus:border-red-600 h-12 text-lg"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white py-6 text-xl font-bold rounded-lg transition-all duration-200 shadow-lg hover:shadow-red-600/50 group disabled:opacity-50"
                >
                  {isSubmitting ? 'Submitting...' : (
                    <>
                      Claim 20% Offer Now
                      <ArrowRight className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </Button>

                <div className="text-center">
                  <p className="text-gray-400 text-sm">
                    ✅ Instant WhatsApp confirmation
                  </p>
                </div>
              </form>
            </Card>
          </div>

          {/* Urgency Footer */}
          <div className="mt-12 text-center">
            <div className="inline-block bg-yellow-500/20 border-2 border-yellow-500 px-8 py-4 rounded-xl">
              <p className="text-yellow-500 font-bold text-xl">
                ⚠️ Only for Rain Damage Cases
              </p>
              <p className="text-yellow-400 text-sm mt-1">
                Valid for repairs related to recent rain/water damage only
              </p>
            </div>
          </div>

          {/* Benefits */}
          <div className="mt-16 grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="bg-red-600/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-8 h-8 text-red-600" />
              </div>
              <p className="text-white font-semibold">German Car Specialists</p>
            </div>
            <div className="text-center">
              <div className="bg-red-600/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-8 h-8 text-red-600" />
              </div>
              <p className="text-white font-semibold">Same Day Service</p>
            </div>
            <div className="text-center">
              <div className="bg-red-600/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-8 h-8 text-red-600" />
              </div>
              <p className="text-white font-semibold">Warranty Included</p>
            </div>
            <div className="text-center">
              <div className="bg-red-600/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-8 h-8 text-red-600" />
              </div>
              <p className="text-white font-semibold">Expert Technicians</p>
            </div>
          </div>

          {/* All Brands Section */}
          <div className="mt-20 mb-12">
            <Card className="bg-gradient-to-br from-zinc-900 to-black border-2 border-red-600 p-8 md:p-12 max-w-5xl mx-auto">
              <div className="text-center">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                  German Car Specialist – We Service All Brands
                </h2>
                <p className="text-gray-400 text-lg mb-8">
                  Expert repair services for all major car brands
                </p>
                
                <div className="flex flex-wrap justify-center items-center gap-6 md:gap-8">
                  <div className="bg-zinc-800 border border-zinc-700 px-6 py-3 rounded-lg hover:border-red-600 transition-all duration-300">
                    <span className="text-white font-bold text-lg">BMW</span>
                  </div>
                  <div className="bg-zinc-800 border border-zinc-700 px-6 py-3 rounded-lg hover:border-red-600 transition-all duration-300">
                    <span className="text-white font-bold text-lg">Mercedes-Benz</span>
                  </div>
                  <div className="bg-zinc-800 border border-zinc-700 px-6 py-3 rounded-lg hover:border-red-600 transition-all duration-300">
                    <span className="text-white font-bold text-lg">Audi</span>
                  </div>
                  <div className="bg-zinc-800 border border-zinc-700 px-6 py-3 rounded-lg hover:border-red-600 transition-all duration-300">
                    <span className="text-white font-bold text-lg">Porsche</span>
                  </div>
                  <div className="bg-zinc-800 border border-zinc-700 px-6 py-3 rounded-lg hover:border-red-600 transition-all duration-300">
                    <span className="text-white font-bold text-lg">Volkswagen</span>
                  </div>
                  <div className="bg-zinc-800 border border-zinc-700 px-6 py-3 rounded-lg hover:border-red-600 transition-all duration-300">
                    <span className="text-white font-bold text-lg">Toyota</span>
                  </div>
                  <div className="bg-zinc-800 border border-zinc-700 px-6 py-3 rounded-lg hover:border-red-600 transition-all duration-300">
                    <span className="text-white font-bold text-lg">Nissan</span>
                  </div>
                  <div className="bg-zinc-800 border border-zinc-700 px-6 py-3 rounded-lg hover:border-red-600 transition-all duration-300">
                    <span className="text-white font-bold text-lg">Hyundai</span>
                  </div>
                </div>

                <p className="text-gray-400 mt-8 text-sm">
                  + Many More Brands Available
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zinc-950 border-t border-zinc-800 py-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400 mb-2">📍 Ras Al Khor Industrial Area 1, Dubai, UAE</p>
          <p className="text-gray-400 mb-4">📧 info@thinkmechanicdxb.com | 📞 056 667 1468</p>
          <p className="text-gray-500 text-sm">© 2024 Think Mechanic. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};
